/**
 * One tile: header, chart, and the states a chart can be in before it is one.
 *
 * **Fetches only once visible.** A dashboard is allowed to be longer than the
 * screen, and the backend is a single task - so a tile below the
 * fold costs nothing until it is scrolled to. Together with the queue in
 * `tile-fetch.ts` this is what keeps a twenty-tile dashboard from behaving like
 * a denial-of-service attack on its own API.
 *
 * The chart mount is a bare div Lit never interpolates into, the same contract
 * `<plot-chart>` has with `#chart-root`: uPlot owns its children and they must
 * survive re-renders.
 */

import { html, nothing, svg, type PropertyValues, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type uPlot from 'uplot';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { renderTile, tileSupports, type TileChart, type XWindow } from './render-tile.ts';
import { fmtTimeShort, formatNum } from '../plot/render/format.ts';
import { fetchTile } from './tile-fetch.ts';
import {
  resolveTile,
  tileHasPeriodComparison,
  tileSummary,
  type ResolvedTile,
  type SharedContext,
} from './tile-spec.ts';
import { LINK_ATOMS, linkState, retryLink, tileWithChart, type LinkState } from './linked-chart.ts';
import { activeDashboard, type DashboardTile, type LiveRefresh } from './dashboard-state.ts';
import { sourceHasHistory } from '../../state/data-sources.ts';
import { drawLiveOverlay, reserveLiveRoom, tagSeriesOf } from '../plot/render/last-value-tags.ts';
import { appendXY, FULL_EVERY, OVERLAP_S } from '../plot/live-tick.ts';
import { isXY } from '../../api/plot-shapes.ts';
import type { AnyPlotResponse, PlotResponse } from '../../api/types.ts';
import type { LiveLayout } from '../../state/plot-state.ts';
import { ROLLING_PRESET_MINUTES, fmtUtcDatetime } from '../../utils/dates.ts';
import '../../ui/live-chip.ts';
import { userMessage } from '../../api/errors.ts';

type TileStatus = 'idle' | 'loading' | 'ready' | 'empty' | 'error' | 'unsupported' | 'link';

/** Chain link: this tile draws a catalog chart. Stroke on `currentColor`. */
const LINK_GLYPH = svg`<svg class="dash-tile-glyph" viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <path
    d="M10 14a4.5 4.5 0 0 0 6.4 0l3.2-3.2a4.5 4.5 0 0 0-6.4-6.4l-1.3 1.3M14 10a4.5 4.5 0 0 0-6.4 0l-3.2 3.2a4.5 4.5 0 0 0 6.4 6.4l1.3-1.3"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>`;

/**
 * Why a tile has nothing to draw, when the tab's slicers are the reason: they
 * applied to it, and no reading was left. Null otherwise - including a gauge
 * whose columns are missing, which draws its own explanation.
 */
export function slicerEmptyMessage(data: AnyPlotResponse, resolved: ResolvedTile): string | null {
  const slicers = resolved.shaping.slicers ?? [];
  if (!slicers.length || data.source_rows) return null;
  if (data.plot_type === 'gauge' && data.dials.some((d) => d.missing?.length)) return null;
  const columns = [...new Set(slicers.map((s) => s.column))];
  const filters = columns.length > 1 ? 'filters' : 'filter';
  return `No readings match the ${filters} on ${columns.join(', ')} in this window.`;
}

/** A funnel: the tab's slicers. Stroke on `currentColor`. */
const FUNNEL_GLYPH = svg`<svg class="dash-tile-glyph" viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <path
    d="M4 5h16l-6 7.5V19l-4 1.5v-8L4 5z"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
  <path class="dash-tile-strike" d="M3 3l18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
</svg>`;

/** The same chain, cut: Detach. */
const UNLINK_GLYPH = svg`<svg class="dash-tile-glyph" viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <path
    d="M15.5 12.5l2.1-2.1a4.5 4.5 0 0 0-6.4-6.4L9.9 5.3M8.5 11.5l-2.1 2.1a4.5 4.5 0 0 0 6.4 6.4l1.3-1.3M4 4l16 16"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>`;

/** What a linked tile says when its chart is not there to draw. */
function linkMessage(link: LinkState): string {
  switch (link.kind) {
    case 'loading':
      return 'Loading catalog chart…';
    case 'missing':
      return 'This catalog chart was deleted or is no longer shared with you.';
    case 'unavailable':
      return 'The catalog is unavailable, so this linked chart cannot load.';
    case 'error':
      return `Could not load the catalog chart: ${link.message}`;
    default:
      return '';
  }
}

/**
 * Every mounted tile, so a theme change can redraw all of them.
 *
 * uPlot bakes its colours in at build time, so the Explore chart has always
 * needed an explicit redraw on a theme toggle (`rerenderChartFromLast`). Tiles
 * need the same, and there is no theme atom to subscribe to - `app-root` calls
 * `redrawDashboardTiles()` from the toggle handler.
 */
const liveTiles = new Set<DashboardTileEl>();

export function redrawDashboardTiles(): void {
  for (const el of liveTiles) el.redraw();
}

/** Set while one tile's zoom is carried to the others, so their echoes stay local. */
let linkingZoom = false;

/**
 * With the shared crosshair on, a zoom is shared the way the crosshair is:
 * every tile with the same X - all the time-series tiles, or the XY tiles on
 * the same column - follows. Off, each tile zooms alone.
 */
function linkZoom(source: DashboardTileEl, win: XWindow | null): void {
  const key = source.syncKey;
  const group = source.xGroup();
  if (!key || !group) return;
  linkingZoom = true;
  try {
    for (const el of liveTiles) {
      if (el !== source && el.syncKey === key && el.xGroup() === group) el.applyZoom(win);
    }
  } finally {
    linkingZoom = false;
  }
}

/**
 * A `decorate` that draws a live tile's newest values in its chosen layout -
 * at the line's end, big over veiled lines, or on a NOW line - as Explore's
 * live chart does (`render/last-value-tags.ts`).
 */
function liveOverlay(xIsTime: boolean, layout: LiveLayout, nowLabel: boolean): (opts: uPlot.Options) => void {
  return (opts) => {
    reserveLiveRoom(opts, layout, nowLabel);
    opts.hooks = {
      ...opts.hooks,
      draw: [
        ...(opts.hooks?.draw ?? []),
        (u) => drawLiveOverlay(u, tagSeriesOf(u), { xIsTime, layout, nowLabel, nowS: Date.now() / 1000 }),
      ],
    };
  };
}

/* ── The Live loop ────────────────────────────────────────── */

/*
 * One loop for the whole dashboard, not a timer per tile. Once a second it
 * looks at every Live tile and starts the ones that are due - at most
 * `TICKS_PER_PASS` per pass, so ten tiles that loaded together drift apart
 * instead of hitting the Live API in step - and they still take turns through
 * `tile-fetch.ts`'s queue. Between ticks, every animation frame moves each
 * following tile's axis to now (one-second steps under reduced motion).
 *
 * A tick never undoes the viewer: a tile that is hovered, zoomed, off screen,
 * or on a hidden browser tab waits, and the others carry on.
 */

const LOOP_MS = 1000;
const TICKS_PER_PASS = 2;
/** "As written" when a tile's data is too thin to say how often it is written. */
const DEFAULT_CADENCE_S = 10;

let loopTimer: number | undefined;
let raf = 0;
let lastRefresh: LiveRefresh | null = null;

function refreshSetting(): LiveRefresh {
  return activeDashboard()?.settings.liveRefresh ?? 'cadence';
}

function livePass(): void {
  const refresh = refreshSetting();
  // Switching refresh on or off changes whether axes follow: redraw so each
  // live tile is built on the right window.
  if (lastRefresh !== null && refresh !== lastRefresh) {
    for (const el of liveTiles) if (el.isLiveTile()) el.redraw();
  }
  lastRefresh = refresh;
  if (refresh === 'off' || (typeof document !== 'undefined' && document.hidden)) return;
  const now = Date.now();
  const due = [...liveTiles].filter((el) => el.liveDue(now, refresh)).sort((a, b) => a.lastFetchAt - b.lastFetchAt);
  for (const el of due.slice(0, TICKS_PER_PASS)) void el.liveTick();
}

function followFrame(): void {
  raf = requestAnimationFrame(followFrame);
  if (typeof document !== 'undefined' && document.hidden) return;
  for (const el of liveTiles) el.followClock();
}

/** Start the loop when the first Live tile appears, stop it with the last. */
function syncLoop(): void {
  const any = [...liveTiles].some((el) => el.isLiveTile());
  if (any && loopTimer === undefined) {
    loopTimer = window.setInterval(livePass, LOOP_MS);
    if (typeof requestAnimationFrame === 'function') raf = requestAnimationFrame(followFrame);
  } else if (!any && loopTimer !== undefined) {
    window.clearInterval(loopTimer);
    loopTimer = undefined;
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    lastRefresh = null;
  }
}

/** Seconds between a tile's newest rows - how often its data is written. */
function writtenEvery(data: PlotResponse): number {
  const xs = data.x;
  const gaps: number[] = [];
  for (let i = xs.length - 1; i > 0 && gaps.length < 20; i--) {
    const g = xs[i]! - xs[i - 1]!;
    if (g > 0) gaps.push(g);
  }
  if (gaps.length < 3) return DEFAULT_CADENCE_S;
  gaps.sort((a, b) => a - b);
  return Math.min(60, Math.max(5, gaps[Math.floor(gaps.length / 2)]!));
}

@customElement('dashboard-tile')
export class DashboardTileEl extends LightElement {
  @property({ attribute: false }) tile!: DashboardTile;
  @property({ attribute: false }) shared!: SharedContext;
  @property({ type: Boolean }) editing = false;
  /** Cursor-sync key for the shared crosshair, or null when it is off. */
  @property({ attribute: false }) syncKey: string | null = null;
  /** With `syncKey`, show the tooltip at a crosshair that came from another tile. */
  @property({ type: Boolean }) sharedTooltip = false;

  /** Re-renders a linked tile as its chart resolves or changes in the catalog. */
  private links = new StoreController(this, [...LINK_ATOMS]);

  @state() private status: TileStatus = 'idle';
  @state() private message = '';
  @state() private warning: string | null = null;
  /** The tile is empty because this tab's slicers removed every reading. */
  @state() private emptyBySlicers = false;
  /**
   * The x window the viewer dragged to, or null for the whole of the data. A
   * view, not a query: it survives a redraw (theme, crosshair) and is dropped
   * when the tile fetches again.
   */
  @state() private zoom: XWindow | null = null;

  private chart: TileChart | null = null;
  /** Bumped per draw so a map that finishes loading Leaflet late is discarded. */
  private drawToken = 0;
  private data: Awaited<ReturnType<typeof fetchTile>> | null = null;
  /** Bumped per load so a slow response from a stale config is discarded. */
  private token = 0;
  private io: IntersectionObserver | null = null;
  private ro: ResizeObserver | null = null;
  private seen = false;
  /** In or near the viewport now - a Live tile off screen does not tick. */
  private onScreen = false;
  /** Pointer over the tile: a tick waits so it does not move under the reader. */
  private pointerIn = false;
  /** When this tile last asked for data (ms), for the Live loop's due check. */
  lastFetchAt = 0;
  private liveTicks = 0;
  private ticking = false;
  /** Signature of the config the current draw came from, to detect real edits. */
  private drawnFor = '';

  override connectedCallback(): void {
    super.connectedCallback();
    liveTiles.add(this);
    syncLoop();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    liveTiles.delete(this);
    syncLoop();
    this.io?.disconnect();
    this.io = null;
    this.ro?.disconnect();
    this.ro = null;
    this.destroyChart();
  }

  override firstUpdated(): void {
    // Load a little before the tile actually reaches the viewport, so scrolling
    // down a dashboard does not feel like it starts from scratch each screen.
    this.io = new IntersectionObserver(
      (entries) => {
        this.onScreen = entries.some((e) => e.isIntersecting);
        if (!this.onScreen) return;
        this.seen = true;
        if (this.status === 'idle') void this.load();
      },
      { rootMargin: '300px' },
    );
    this.io.observe(this);
    this.addEventListener('pointerenter', () => (this.pointerIn = true));
    this.addEventListener('pointerleave', () => (this.pointerIn = false));

    const mount = this.chartMount();
    if (mount) {
      this.ro = new ResizeObserver(() => this.fit());
      this.ro.observe(mount);
    }
  }

  override updated(changed: PropertyValues<this>): void {
    // A config change is a refetch; a size change is only a resize. Comparing
    // the resolved signature rather than object identity matters because the
    // parent re-renders (and so re-assigns `tile`) on every drag frame.
    const sig = this.configSignature();
    if (sig !== this.drawnFor && this.seen) {
      this.drawnFor = sig;
      void this.load();
      return;
    }
    // Crosshair sharing is baked into the uPlot instance: a redraw from the
    // data already here, not a refetch.
    if (
      (changed.has('syncKey') && changed.get('syncKey') !== undefined) ||
      (changed.has('sharedTooltip') && changed.get('sharedTooltip') !== undefined)
    ) {
      this.redraw();
    }
  }

  private configSignature(): string {
    if (!this.tile) return '';
    const link = this.link();
    if (link && link.kind !== 'ready') return `link:${link.kind}`;
    const tile = this.drawnTile();
    const r = resolveTile(tile, this.shared);
    // A rolling window moves on every resolve: its name is the recipe, and the
    // Live loop is what moves it on (`live-board.ts`).
    const window = r.rolling ?? [r.start, r.end];
    return JSON.stringify([tile.query, tile.bind, tile.source, r.ships, window, r.shaping]);
  }

  /**
   * Why a tile is showing more than the tab's slicers ask for, or '' when it is
   * not. Only spoken when there is a selection to disagree with.
   */
  private slicerNote(): string {
    if (!this.tile || !this.shared?.slicers?.length) return '';
    if (this.tile.ignoreSlicers) return "Not narrowed by this tab's slicers";
    if (resolveTile(this.drawnTile(), this.shared).slicersUnapplied) {
      return "Not narrowed by this tab's slicers - the tile's own filters use OR";
    }
    return '';
  }

  /** The linked chart's state, or null for a tile that carries its own recipe. */
  private link(): LinkState | null {
    return this.tile?.chartId ? linkState(this.tile.chartId) : null;
  }

  /** The recipe to draw: the linked chart's current one when it resolves. */
  private drawnTile(): DashboardTile {
    const link = this.link();
    return link?.kind === 'ready' ? tileWithChart(this.tile, link.chart) : this.tile;
  }

  private chartMount(): HTMLElement | null {
    return this.querySelector<HTMLElement>('.dash-tile-chart');
  }

  private destroyChart(): void {
    this.drawToken++;
    this.chart?.destroy();
    this.chart = null;
  }

  /* ── Load ─────────────────────────────────────────────── */

  async load(): Promise<void> {
    if (!this.tile) return;
    const token = ++this.token;
    this.drawnFor = this.configSignature();

    const link = this.link();
    if (link && link.kind !== 'ready') {
      this.destroyChart();
      this.status = 'link';
      return;
    }

    const drawnTile = this.drawnTile();
    if (tileHasPeriodComparison(drawnTile)) {
      this.destroyChart();
      this.status = 'unsupported';
      this.message = 'Open in Explore to compare periods.';
      return;
    }
    const resolved = resolveTile(drawnTile, this.shared);
    if (!tileSupports(resolved.state.plot_type)) {
      this.destroyChart();
      this.status = 'unsupported';
      this.message = `${resolved.state.plot_type} tiles are not supported yet - open in Explore.`;
      return;
    }
    if (!resolved.ships.length) {
      this.destroyChart();
      this.status = 'empty';
      this.message = 'Pick a ship in the toolbar.';
      return;
    }

    this.status = 'loading';
    // New data, new extent: a window from the last draw means nothing now.
    this.zoom = null;
    this.warning = null;
    this.emptyBySlicers = false;
    this.lastFetchAt = Date.now();
    this.liveTicks = 0;
    try {
      const result = await fetchTile(resolved);
      if (token !== this.token) return;
      this.data = result;
      syncLoop();
      this.warning = result.warning ?? null;
      const blank = slicerEmptyMessage(result.data, resolved);
      if (blank) {
        // An empty chart says nothing; say which filters left nothing to draw.
        this.destroyChart();
        this.status = 'empty';
        this.message = blank;
        this.emptyBySlicers = true;
        return;
      }
      this.status = 'ready';
      await this.updateComplete;
      this.draw();
    } catch (e) {
      if (token !== this.token) return;
      this.destroyChart();
      this.status = 'error';
      this.message = userMessage(e);
    }
  }

  /* ── Draw ─────────────────────────────────────────────── */

  private draw(): void {
    void this.drawAsync();
  }

  /**
   * Draw from the data already here.
   *
   * A live chart is offered the new data first: a map takes it and keeps the
   * view the viewer panned to, while every uPlot chart declines and is rebuilt,
   * which is what a redraw has always been. Async because a map lazy-loads
   * Leaflet, so a stale draw is dropped on the token rather than mounting a
   * second map on the same node.
   */
  private async drawAsync(): Promise<void> {
    const mount = this.chartMount();
    const tooltip = this.querySelector<HTMLElement>('.uplot-tooltip');
    const container = this.querySelector<HTMLElement>('.dash-tile-body');
    if (!mount || !tooltip || !container || !this.data || !this.tile) return;

    const resolved = resolveTile(this.drawnTile(), this.shared);
    if (this.chart?.update(this.data.data, resolved.state)) {
      this.fit();
      return;
    }

    // The token is taken *after* `destroyChart`, which bumps it to abandon any
    // draw still in flight - taking it first would abandon this one.
    this.destroyChart();
    mount.innerHTML = '';
    const token = this.drawToken;
    const size = {
      width: Math.max(80, mount.clientWidth),
      height: Math.max(60, mount.clientHeight),
    };
    const chart = await renderTile(
      { mount, tooltip, container },
      this.data.data,
      resolved.state,
      size,
      {
        syncKey: this.syncKey,
        sharedTooltip: this.sharedTooltip,
        ...(this.zoomable()
          ? { zoom: { initial: this.zoom, onChange: (win: XWindow | null) => this.onZoom(win) } }
          : {}),
        ...(this.isLive() && this.data.data.plot_type === 'line'
          ? {
              decorate: liveOverlay(
                !!this.data.data.x_is_time,
                resolved.state.style.live_layout,
                resolved.state.style.live_now_label,
              ),
            }
          : {}),
        ...(this.isLive() ? { clock: () => this.clockWindow() } : {}),
      },
    );
    if (token !== this.drawToken) {
      chart?.destroy();
      return;
    }
    this.chart = chart;
    // Second pass: uPlot has built its legend now, so the height it was given
    // can be corrected before paint. Same two-step the single chart and the
    // grid both use - guessing the legend height is what produces scrollbars.
    this.fit();
  }

  /* ── Live ─────────────────────────────────────────────── */

  /** True when the tile reads a source without history - Live. */
  private isLive(): boolean {
    return !!this.tile && !sourceHasHistory(this.tile.source);
  }

  /** For the Live loop: a drawn tile on a source without history. */
  isLiveTile(): boolean {
    return this.isLive() && this.status === 'ready' && !!this.data;
  }

  /**
   * The window a following tile shows now: its rolling preset back from now
   * (and as far ahead for the `now` layout), or null when it does not follow -
   * refresh is off, the window is fixed, or the x axis is not time.
   */
  private clockWindow(): XWindow | null {
    const d = this.data?.data;
    if (!d || !isXY(d) || !d.x_is_time || refreshSetting() === 'off') return null;
    const r = resolveTile(this.drawnTile(), this.shared);
    const minutes = r.rolling ? ROLLING_PRESET_MINUTES[r.rolling] : undefined;
    if (minutes === undefined) return null;
    const span = minutes * 60;
    // Each tile moves as its chart says (⚙ Motion): smooth, or whole seconds.
    const nowS = r.state.style.live_motion === 'smooth' ? Date.now() / 1000 : Math.floor(Date.now() / 1000);
    return { min: nowS - span, max: nowS + (r.state.style.live_layout === 'now' ? span : 0) };
  }

  /** Why this tile cannot move on right now, or null when it can. */
  private liveHeld(): boolean {
    return !this.onScreen || this.pointerIn || this.zoom !== null || this.editing || this.ticking;
  }

  /** Whether the Live loop should tick this tile now. */
  liveDue(now: number, refresh: LiveRefresh): boolean {
    if (!this.isLiveTile() || this.liveHeld()) return false;
    if (!resolveTile(this.drawnTile(), this.shared).rolling) return false;
    const d = this.data!.data;
    const everyS = refresh === 'cadence' ? (isXY(d) ? writtenEvery(d) : DEFAULT_CADENCE_S) : Number(refresh);
    return now - this.lastFetchAt >= everyS * 1000;
  }

  /**
   * One animation frame: put a following tile's axis on now - every frame,
   * on each new second (1 s steps), or never between ticks (Off: a tick's
   * redraw is built on now).
   */
  followClock(): void {
    if (!this.isLiveTile() || this.pointerIn || this.zoom !== null) return;
    const u = this.chart?.uplot;
    const win = this.clockWindow();
    if (!u || !win) return;
    const mode = resolveTile(this.drawnTile(), this.shared).state.style.live_motion;
    if (mode === 'off') return;
    if (mode === 'steps' && u.scales.x?.min === win.min) return;
    u.setScale('x', win);
  }

  /**
   * Move on: the window rolls to now, then a line or scatter tile asks only for
   * the rows after its newest (less `OVERLAP_S`, for rows that land late) and
   * merges them in, while everything else - and every `FULL_EVERY`th tick -
   * refetches the window. The tile redraws from the result without passing
   * through "loading", so it never blanks.
   */
  async liveTick(): Promise<void> {
    if (this.ticking || !this.data) return;
    this.ticking = true;
    this.lastFetchAt = Date.now();
    const token = this.token;
    try {
      const resolved = resolveTile(this.drawnTile(), this.shared);
      const prev = this.data.data;
      const s = resolved.state;
      const appendable =
        isXY(prev) &&
        prev.x_is_time &&
        prev.last_x != null &&
        !s.fits.length &&
        !resolved.shaping.derived?.length &&
        (resolved.shaping.outliers?.method ?? 'none') === 'none';
      let next: Awaited<ReturnType<typeof fetchTile>>;
      if (appendable && ++this.liveTicks % FULL_EVERY !== 0) {
        const windowStart = Date.parse(`${resolved.start}Z`) / 1000;
        const from = Math.max(windowStart, (prev as PlotResponse).last_x! - OVERLAP_S);
        let delta: Awaited<ReturnType<typeof fetchTile>> | null = null;
        try {
          delta = await fetchTile({ ...resolved, start: fmtUtcDatetime(new Date(from * 1000)) }, true);
        } catch {
          // An empty window is "no new rows", not an error worth a redraw.
        }
        if (!delta || !isXY(delta.data)) return;
        next = { ...this.data, data: appendXY(prev as PlotResponse, delta.data, from, windowStart) };
      } else {
        next = await fetchTile(resolved, true);
      }
      // A config change or a full load since this tick started wins.
      if (token !== this.token) return;
      this.data = next;
      this.warning = next.warning ?? null;
      this.draw();
    } catch {
      // A failed tick leaves the tile as it was; the chip's age says data has
      // stopped arriving, and the next tick tries again.
    } finally {
      this.ticking = false;
    }
  }

  /** "LIVE · 12 s ago" in the header of a live tile that has drawn. */
  private renderLiveChip(): TemplateResult | typeof nothing {
    if (!this.isLive() || !this.data) return nothing;
    const d = this.data.data;
    const newest = (d.plot_type === 'line' || d.plot_type === 'scatter') && d.x_is_time ? d.last_x : null;
    return html`<live-chip .newest=${newest}></live-chip>`;
  }

  /* ── Zoom ─────────────────────────────────────────────── */

  /** Line and Scatter tiles zoom; the rest have no x range to drag over. */
  private zoomable(): boolean {
    const pt = this.data?.data.plot_type;
    return pt === 'line' || pt === 'scatter';
  }

  /** Which X this tile draws, for linking zooms: time, or a named column. */
  xGroup(): string | null {
    const d = this.data?.data;
    if (!d || (d.plot_type !== 'line' && d.plot_type !== 'scatter')) return null;
    return d.x_is_time ? 'time' : `x:${d.x_column ?? ''}`;
  }

  private onZoom(win: XWindow | null): void {
    const was = this.zoom;
    this.zoom = win;
    // A Live tile's axis moving with the clock reports "not zoomed" every
    // frame; only a real change is carried to the other tiles.
    if (was === null && win === null) return;
    if (!linkingZoom) linkZoom(this, win);
  }

  /** Follow another tile's zoom. */
  applyZoom(win: XWindow | null): void {
    if (!this.chart?.setZoom) return;
    this.chart.setZoom(win);
  }

  private resetZoom(): void {
    if (this.chart?.setZoom) this.chart.setZoom(null);
    else this.zoom = null;
  }

  /** The zoomed window's two ends, as Explore's readout shows them. */
  private zoomEnds(): [string, string] {
    const z = this.zoom;
    const d = this.data?.data;
    if (!z || !d || (d.plot_type !== 'line' && d.plot_type !== 'scatter')) return ['', ''];
    const f = (v: number): string => (d.x_is_time ? fmtTimeShort(v) : formatNum(v));
    return [f(z.min), f(z.max)];
  }

  /* ── Export ───────────────────────────────────────────── */

  /**
   * Load now rather than when scrolled into view. A dashboard export draws
   * every tile, including the ones below the fold that viewport-lazy loading
   * has not fetched yet. Still goes through the request queue.
   */
  prepareForExport(): void {
    this.seen = true;
    if (this.status === 'idle') void this.load();
  }

  /** Still on its way to something drawable - data, or its linked chart. */
  get exportBusy(): boolean {
    if (this.status === 'idle' || this.status === 'loading') return true;
    return this.status === 'link' && this.link()?.kind === 'loading';
  }

  /** Redraw from cached data - a theme change, not a refetch. */
  redraw(): void {
    if (this.status === 'ready' && this.data) this.draw();
  }

  private fit(): void {
    const mount = this.chartMount();
    if (!this.chart || !mount) return;
    // Hidden (another section is open): keep the size it has. Shrinking to the
    // minimum wrapped the legend into a tall column, and the fit on the way
    // back measured that column and squashed the plot until the next redraw.
    if (!mount.clientWidth || !mount.clientHeight) return;
    const width = Math.max(80, mount.clientWidth);
    const height = (): number => Math.max(60, mount.clientHeight - this.legendHeight());
    const first = height();
    this.chart.resize({ width, height: first });
    // The legend wraps to the width it was laid out at, so a chart drawn while
    // hidden measures a taller legend before the new width than after it.
    const settled = height();
    if (settled !== first) this.chart.resize({ width, height: settled });
  }

  private legendHeight(): number {
    const legend = this.chart?.legendEl();
    if (!legend || legend.offsetParent === null) return 0;
    const cs = getComputedStyle(legend);
    return (
      Math.ceil(legend.getBoundingClientRect().height) +
      Math.ceil(parseFloat(cs.marginTop) || 0) +
      Math.ceil(parseFloat(cs.marginBottom) || 0)
    );
  }

  /* ── Actions ──────────────────────────────────────────── */

  private emit(name: string): void {
    this.dispatchEvent(
      new CustomEvent(name, { detail: { id: this.tile.id }, bubbles: true, composed: true }),
    );
  }

  /* ── Render ───────────────────────────────────────────── */

  private renderState(): TemplateResult | typeof nothing {
    if (this.status === 'ready') return nothing;
    if (this.status === 'link') return this.renderLinkState();
    if (this.status === 'loading' || this.status === 'idle') {
      return html`<div class="dash-tile-state">
        <span class="spinner spinner-md" aria-hidden="true"></span>
        <span>${this.status === 'idle' ? 'Waiting…' : 'Loading…'}</span>
      </div>`;
    }
    return html`<div class="dash-tile-state ${this.status === 'error' ? 'is-error' : ''}">
      <span>${this.message}</span>
      ${this.status === 'error'
        ? html`<button class="dash-tile-retry" @click=${() => void this.load()}>Retry</button>`
        : nothing}
      ${this.status === 'empty' && this.emptyBySlicers
        ? html`<button
            class="dash-tile-retry"
            title="Clear this tab's filters and show everything in the dashboard's time"
            @click=${() => this.emit('tile-clear-slicers')}
          >
            Clear filters
          </button>`
        : nothing}
    </div>`;
  }

  /**
   * A linked tile whose chart is not there. Detach is offered right here, not
   * only in Edit mode: when the chart has gone it is the one way to keep
   * the tile, and the recipe it was linked with is still on it.
   */
  private renderLinkState(): TemplateResult {
    const link = this.link();
    if (!link || link.kind === 'ready') return html``;
    if (link.kind === 'loading') {
      return html`<div class="dash-tile-state">
        <span class="spinner spinner-md" aria-hidden="true"></span>
        <span>${linkMessage(link)}</span>
      </div>`;
    }
    const chartId = this.tile.chartId;
    return html`<div class="dash-tile-state ${link.kind === 'error' ? 'is-error' : ''}">
      <span>${linkMessage(link)}</span>
      <span class="dash-tile-state-actions">
        ${link.kind === 'error' && chartId
          ? html`<button class="dash-tile-retry" @click=${() => retryLink(chartId)}>Retry</button>`
          : nothing}
        ${link.kind !== 'error'
          ? html`<button
              class="dash-tile-retry"
              title="Keep this tile as its own copy of the chart it was linked with"
              @click=${() => this.emit('tile-detach')}
            >
              Keep a copy
            </button>`
          : nothing}
      </span>
    </div>`;
  }

  /** Hover text on the link glyph: whose chart, and what linking means. */
  private linkTitle(): string {
    const link = this.link();
    if (link?.kind !== 'ready') return 'Linked to a catalog chart';
    const chart = link.chart;
    const owner = chart.is_owner ? '' : ` by ${chart.owner.display_name}`;
    return `Linked to "${chart.title}"${owner} in the catalog - edits to it show up here`;
  }

  override render(): TemplateResult {
    if (!this.tile) return html``;
    const linked = !!this.tile.chartId;
    const shown = this.drawnTile();
    return html`
      <div class="dash-tile">
        <div class="dash-tile-head" data-drag-handle>
          <div class="dash-tile-heading">
            <span class="dash-tile-title" title=${`${shown.title} - ${tileSummary(shown)}`}>
              ${linked
                ? html`<span class="dash-tile-link" title=${this.linkTitle()}>${LINK_GLYPH}</span>`
                : nothing}
              ${shown.title}
            </span>
          </div>
          <div class="dash-tile-actions">
            ${this.renderLiveChip()}
            ${this.zoom
              ? html`<span class="plot-x-window is-zoomed" title="Zoomed - drag across the chart to zoom further"
                    >${this.zoomEnds()[0]}<span class="pxw-sep">→</span>${this.zoomEnds()[1]}</span
                  ><button
                    class="dash-tile-btn"
                    aria-label="Reset zoom - back to the full range (same as a double-click)"
                    @click=${() => this.resetZoom()}
                  >
                    ⤢
                  </button>`
              : nothing}
            ${this.warning
              ? html`<span class="dash-tile-warn" title=${this.warning} aria-label="Warning">!</span>`
              : nothing}
            <button
              class="dash-tile-btn"
              aria-label="Refresh"
              @click=${() => void this.load()}
            >
              ⟳
            </button>
            <button
              class="dash-tile-btn"
              aria-label="Open in Explore"
              @click=${() => this.emit('tile-open')}
            >
              ↗
            </button>
            ${this.editing
              ? html`
                  <button
                    class="dash-tile-btn ${this.tile.ignoreSlicers ? 'is-off' : ''}"
                    aria-label=${this.tile.ignoreSlicers
                      ? "This tile ignores the tab's slicers - click to let them narrow it"
                      : "Click to make this tile ignore the tab's slicers"}
                    aria-pressed=${this.tile.ignoreSlicers ? 'true' : 'false'}
                    @click=${() => this.emit('tile-slicers')}
                  >
                    ${FUNNEL_GLYPH}
                  </button>
                  <button
                    class="dash-tile-btn"
                    aria-label="Duplicate"
                    @click=${() => this.emit('tile-duplicate')}
                  >
                    ⧉
                  </button>
                  ${linked
                    ? html`<button
                        class="dash-tile-btn"
                        aria-label="Detach from the catalog - make this tile its own editable copy"
                        data-tour="tile-detach"
                        @click=${() => this.emit('tile-detach')}
                      >
                        ${UNLINK_GLYPH}
                      </button>`
                    : html`<button
                        class="dash-tile-btn"
                        aria-label="Edit chart"
                        data-tour="tile-edit"
                        @click=${() => this.emit('tile-edit')}
                      >
                        ✎
                      </button>`}
                  <button
                    class="dash-tile-btn is-danger"
                    aria-label="Remove"
                    @click=${() => this.emit('tile-remove')}
                  >
                    ✕
                  </button>
                `
              : nothing}
          </div>
        </div>
        <div class="dash-tile-body">
          ${this.slicerNote()
            ? html`<span
                class="dash-tile-slicer-note"
                role="img"
                title=${this.slicerNote()}
                aria-label=${this.slicerNote()}
                >${FUNNEL_GLYPH}</span
              >`
            : nothing}
          <div class="dash-tile-chart"></div>
          <div class="uplot-tooltip"></div>
          ${this.renderState()}
        </div>
        ${this.editing ? html`<div class="dash-tile-resize" data-resize-handle></div>` : nothing}
      </div>
    `;
  }
}
